import type { InputMode, ToneShape } from './types';

export interface RangeSpec {
  min: number;
  max: number;
  step: number;
}

export const RANGES = {
  toneHz: { min: 40, max: 2000, step: 1 },
  toneDb: { min: -60, max: 0, step: 0.1 },
  monitorDb: { min: -60, max: 6, step: 0.1 },
  preampDb: { min: -12, max: 36, step: 0.1 },
  hpfHz: { min: 20, max: 500, step: 1 },
  pan: { min: -1, max: 1, step: 0.01 },
  channelDb: { min: -60, max: 12, step: 0.1 },
  lowHz: { min: 40, max: 400, step: 1 },
  lowGain: { min: -15, max: 15, step: 0.1 },
  midHz: { min: 200, max: 5000, step: 1 },
  midGain: { min: -15, max: 15, step: 0.1 },
  midQ: { min: 0.4, max: 8, step: 0.01 },
  highHz: { min: 2000, max: 16000, step: 10 },
  highGain: { min: -15, max: 15, step: 0.1 },
  gateThreshold: { min: -80, max: 0, step: 0.1 },
  gateAttack: { min: 0.001, max: 0.05, step: 0.001 },
  gateRelease: { min: 0.02, max: 0.5, step: 0.001 },
  compThreshold: { min: -60, max: 0, step: 0.1 },
  compRatio: { min: 1, max: 20, step: 0.1 },
  compAttack: { min: 0.001, max: 0.4, step: 0.001 },
  compRelease: { min: 0.02, max: 1, step: 0.001 },
  compKnee: { min: 0, max: 40, step: 0.1 },
  makeupDb: { min: 0, max: 24, step: 0.1 },
  delayTime: { min: 0.02, max: 1.2, step: 0.001 },
  delayFeedback: { min: 0, max: 0.85, step: 0.01 },
  delayDamp: { min: 800, max: 12000, step: 10 },
  delaySend: { min: 0, max: 1, step: 0.01 },
  reverbDecay: { min: 0.4, max: 5, step: 0.01 },
  reverbSend: { min: 0, max: 1, step: 0.01 },
  masterDb: { min: -60, max: 6, step: 0.1 },
  trackDb: { min: -60, max: 6, step: 0.1 },
  metroBpm: { min: 40, max: 240, step: 1 },
  metroLevel: { min: 0, max: 1, step: 0.01 },
} as const satisfies Record<string, RangeSpec>;

export const DEFAULTS = {
  inputMode: 'tone' as InputMode,
  toneShape: 'sine' as ToneShape,
  toneHz: 220,
  toneDb: -18,
  monitorDb: -60,
  preampDb: 0,
  hpfHz: 20,
  polarity: false,
  pan: 0,
  channelDb: 0,
  lowHz: 120,
  lowGain: 0,
  midHz: 1000,
  midGain: 0,
  midQ: 1,
  highHz: 8000,
  highGain: 0,
  gateOn: false,
  gateThreshold: -48,
  gateAttack: 0.005,
  gateRelease: 0.1,
  compThreshold: -24,
  compRatio: 4,
  compAttack: 0.01,
  compRelease: 0.2,
  compKnee: 12,
  makeupDb: 0,
  delayTime: 0.3,
  delayFeedback: 0.35,
  delayDamp: 4000,
  delaySend: 0,
  reverbDecay: 1.8,
  reverbSend: 0,
  masterDb: 0,
  metroOn: false,
  metroBpm: 100,
  metroLevel: 0.25,
  voiceProcessing: true,
};
